import { useMemo } from 'react';
import { toast } from 'components/global/CUK/Toast/Toast';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { trackEvent } from 'analytics';
import {
  OPERATION_FIND_MESSAGE,
  OPERATION_CREATE_MESSAGE,
  OPERATION_UPDATE_MESSAGE,
  OPERATION_DELETE_MESSAGE,
  OPERATION_ENABLE_MESSAGE,
  OPERATION_DISABLE_MESSAGE,
} from '../../operations-gql';

import { useRoom } from '../../use-rooms';
import useMessages from '../../use-messages';

const useMessage = ({ room: selectedRoom } = {}) => {
  const client = useApolloClient();
  const { t } = useTranslation();
  const routeRoom = useRoom();
  const room = selectedRoom || routeRoom;
  const { messages, loading } = useMessages();

  const message = useMemo(() => {
    if (messages.length > 0) {
      return messages[0] || { content: '' };
    }
    return (!loading && messages[0]) ? messages[0] : { content: '' };
  }, [loading, messages]);

  const enable = async (messageId) => {
    try {
      await client.mutate({
        mutation: OPERATION_ENABLE_MESSAGE,
        variables: {
          sf_id: room.site_sf_id,
          message_id: messageId,
        },
      });
      trackEvent('stream_cast_message_enabled');
      toast.success(t('stream-cast.room_message.toast.enable_success'));
    } catch (e) {
      toast.error(t('stream-cast.room_message.toast.enable_error'));
    }
  };

  const disable = async (messageId) => {
    try {
      await client.mutate({
        mutation: OPERATION_DISABLE_MESSAGE,
        variables: {
          sf_id: room.site_sf_id,
          message_id: messageId,
        },
      });
      trackEvent('stream_cast_message_disabled');
      toast.success(t('stream-cast.room_message.toast.disable_success'));
    } catch (e) {
      toast.error(t('stream-cast.room_message.toast.disable_error'));
    }
  };

  const create = async (content) => {
    const result = await client.mutate({
      mutation: OPERATION_CREATE_MESSAGE,
      variables: {
        sf_id: room.site_sf_id,
        title: 'Title',
        content,
      },
      update: (cache, { data }) => {
        cache.updateQuery(
          {
            query: OPERATION_FIND_MESSAGE,
            variables: {
              sf_id: room.site_sf_id,
            },
          },
          () => {
            return {
              message: data.message,
            };
          },
        );
      },
    });
    trackEvent('stream_cast_message_created');
    return result;
  };

  const remove = async () => {
    await client.mutate({
      mutation: OPERATION_DELETE_MESSAGE,
      variables: {
        sf_id: room.site_sf_id,
        message_id: message.id,
      },
      update: (cache) => {
        cache.updateQuery(
          {
            query: OPERATION_FIND_MESSAGE,
            variables: {
              sf_id: room.site_sf_id,
            },
          },
          () => {
            return {
              message: null,
            };
          },
        );
      },
    });
    trackEvent('stream_cast_message_deleted');
  };

  const update = async (content) => {
    const result = await client.mutate({
      mutation: OPERATION_UPDATE_MESSAGE,
      variables: {
        sf_id: room.site_sf_id,
        message_id: message.id,
        title: 'Titre',
        content,
      },
    });
    trackEvent('stream_cast_message_updated');
    return result;
  };

  const saveContent = async (content) => {
    if (!message.id) {
      try {
        return create(content);
      } catch (e) {
        toast.error(t('stream-cast.room_message.toast.create_error'));
      }
    } else if (content.length === 0) {
      try {
        await remove();
      } catch (e) {
        toast.error(t('stream-cast.room_message.toast.delete_error'));
      }
    } else {
      try {
        return update(content);
      } catch (e) {
        toast.error(t('stream-cast.room_message.toast.update_error'));
      }
    }
  };
  
  return {
    saveContent,
    enable,
    disable,
    room,
    message,
    loading
  };
};

export default useMessage;
